'use client';
import { useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  InputAdornment,
  Menu,
  MenuItem,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';

import { TabButton } from '@/components';
import { useT } from '@/hooks';

interface LeaderboardTableHeaderProps {
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  orderBy: 'PNL' | 'VOL';
  setOrderBy: (val: 'PNL' | 'VOL') => void;
}

export const LeaderboardTableHeader = ({
  searchQuery,
  setSearchQuery,
  orderBy,
  setOrderBy,
}: LeaderboardTableHeaderProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const { t } = useT();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSetOrderByPNL = () => {
    setOrderBy('PNL');
    setAnchorEl(null);
  };

  const handleSetOrderByVOL = () => {
    setOrderBy('VOL');
    setAnchorEl(null);
  };

  const handleMobileMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    // Only open menu on mobile view where the Volume tab is hidden
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // On desktop, the "Profit/Loss" column is strictly PNL.
  // On mobile, the column header label is dynamic based on chosen order.
  const displayMobileLabel = orderBy === 'VOL' ? t('leaderboard.volume') : t('leaderboard.profit');
  const arrowTransformStyle = isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)';

  return (
    <TableHead>
      <TableRow>
        <TableCell
          colSpan={2}
          sx={{
            py: 1,
            px: { xs: 1, sm: 2 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            borderTop: '1px solid',
          }}
        >
          <TextField
            fullWidth
            variant="standard"
            placeholder={t('portfolio.searchPlaceholder', { defaultValue: 'Search' })}
            value={searchQuery || ''}
            onChange={handleSearchChange}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 1.5 }}>
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: '0.9375rem',
                color: 'text.secondary',
              },
            }}
          />
        </TableCell>
        <TableCell
          align="right"
          sx={{
            py: 0,
            px: { xs: 1, sm: 2 },
            position: 'relative',
            borderBottom: '1px solid',
            borderColor: 'grey.300',
            borderTop: '1px solid',
            pr: { xs: 2, sm: 4 },
          }}
        >
          <Box sx={{ display: 'inline-flex', position: 'relative', height: '100%' }}>
            {/* Desktop PNL Button */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <TabButton isActive={orderBy === 'PNL'} onClick={handleSetOrderByPNL}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 1.5 }}>
                  {t('leaderboard.profit')}
                </Box>
              </TabButton>
            </Box>

            {/* Mobile Dynamic Dropdown Button */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <TabButton isActive={true} onClick={handleMobileMenuClick}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 1.5 }}>
                  {displayMobileLabel}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      marginLeft: 2,
                      transition: 'transform 0.2s',
                      transform: arrowTransformStyle,
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </Box>
              </TabButton>
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    minWidth: 140,
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  },
                },
              }}
            >
              <MenuItem
                onClick={handleSetOrderByPNL}
                selected={orderBy === 'PNL'}
                sx={{ fontSize: '0.9375rem', py: 1.5 }}
              >
                {t('leaderboard.profit')}
              </MenuItem>
              <MenuItem
                onClick={handleSetOrderByVOL}
                selected={orderBy === 'VOL'}
                sx={{ fontSize: '0.9375rem', py: 1.5 }}
              >
                {t('leaderboard.volume')}
              </MenuItem>
            </Menu>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '1px',
              height: '20px',
              bgcolor: 'divider',
              display: { xs: 'none', md: 'block' },
            }}
          />
        </TableCell>
        <TableCell
          align="right"
          sx={{
            fontWeight: 500,
            fontSize: '0.875rem',
            color: 'text.secondary',
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'grey.300',
            borderTop: '1px solid',
            width: '120px',
            display: { xs: 'none', md: 'table-cell' },
          }}
        >
          <TabButton isActive={orderBy === 'VOL'} onClick={handleSetOrderByVOL}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 1.5 }}>
              {t('leaderboard.volume')}
            </Box>
          </TabButton>
        </TableCell>
      </TableRow>
    </TableHead>
  );
};
